import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { take } from 'rxjs/operators';
import { NotificationResponse } from '../../../interfaces/responses/models';
import { LocalStorageService } from '../../../services/local-storage.service';
import { ScreenSizeService } from '../../../services/screen-size.service';

@Component({
  selector: 'app-notification',
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.css']
})
export class NotificationComponent {
  isMobile: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public notifications: NotificationResponse[],
    public dialogRef: MatDialogRef<NotificationComponent>,
    private screenSizeService: ScreenSizeService,
    private localStorageService: LocalStorageService,
    private router: Router,
  ) {}

  ngOnInit() {
    // FIX 2: Create a copy of the array before reversing it to prevent modifying the original.
    if (Array.isArray(this.notifications)) {
      this.notifications = [...this.notifications].reverse();
    } else {
      this.notifications = [];
    }

    this.screenSizeService.isMobile$.subscribe(isMobile => {
      this.isMobile = isMobile;
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  async clearNotifications(): Promise<void> {
    this.notifications.length = 0;
    try {
      await this.localStorageService.setNotifications(this.notifications);
      const storedNotifications = await this.localStorageService.getNotifications();
      console.log('Stored notifications after clearing:', storedNotifications);
    } catch (err) {
      console.error('Failed to clear notifications in local storage', err);
    }
  }

  handleNotificationClick(notification: NotificationResponse): void {
    if (!notification) return;

    const msg = (notification.message || '').toLowerCase();
    const urlRaw = (notification.url || '').toString();
    const isPaymentType = notification.type === 'payment';
    const urlContainsPayment = /(^\/?payment\/)|\/payment\//i.test(urlRaw) || urlRaw.toLowerCase().includes('payment');
    const messageIndicatesTokens = msg.includes('added tokens') || msg.includes('tokens added') || msg.includes('successfully added tokens');

    if (isPaymentType || urlContainsPayment || messageIndicatesTokens) {
      const finalTarget = '/user-wallet';

      // FIX 1: If we are already on the target page, just close the dialog.
      if (this.router.url.startsWith(finalTarget)) {
        this.dialogRef.close();
        return;
      }

      // Close dialog then navigate
      this.dialogRef.close();
      this.dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
        this.router.navigateByUrl(finalTarget).then(navigated => {
          if (!navigated) {
            console.warn('Navigation to /wallet failed, falling back to /dashboard');
            this.router.navigate(['/dashboard']);
          }
        }).catch(err => {
          console.error('Navigation error to /wallet:', err);
          this.router.navigate(['/dashboard']);
        });
      });
      return;
    }

    let target = '';
    if (notification.url) {
      target = urlRaw.startsWith('/') ? urlRaw : `/${urlRaw}`;
    } else {
      const related = (notification as any).relatedBid || (notification as any).bid || null;
      if (related?.id) {
        const id = related.id;
        if (msg.includes('strike')) {
          target = `/strike-price/${id}`;
        } else if (msg.includes('fixed-rate') || msg.includes('preferential') || msg.includes('fixed rate')) {
          target = `/fixed-rate-bid/${id}`;
        } else if (msg.includes('accepted') || msg.includes('rejected') || msg.includes('offer')) {
          target = `/fixed-rate-bid/${id}`;
        } else {
          target = `/bid/${id}`;
        }
      } else {
        target = '/dashboard';
      }
    }

    const currentPathOnly = this.router.url.split('?')[0];
    const targetPathOnly = target.split('?')[0];
    let finalTarget = target;
    if (currentPathOnly === targetPathOnly) {
      const sep = target.includes('?') ? '&' : '?';
      finalTarget = `${target}${sep}ts=${Date.now()}`;
    }

    this.dialogRef.close();
    this.dialogRef.afterClosed().pipe(take(1)).subscribe(() => {
      this.router.navigateByUrl(finalTarget).then(navigated => {
        if (!navigated) {
          console.warn('Navigation failed for', finalTarget, 'falling back to /dashboard');
          this.router.navigate(['/dashboard']);
        }
      }).catch(err => {
        console.error('Navigation error:', err);
        this.router.navigate(['/dashboard']);
      });
    });
  }

  getNotificationTitle(message: string): string {
    if (!message) return 'New Notification';
    const m = message.toLowerCase();
    if (m.includes('created')) {
      return 'There is a new offer for you';
    } else if (m.includes('update')) {
      return 'There is an updated offer for you';
    } else if (m.includes('accepted')) {
      return 'Your Bid Was Accepted';
    } else if (m.includes('rejected')) {
      return 'Your Bid Was Rejected';
    } else if (m.includes('reminds') || m.includes('remind')) {
      return 'There is a reminder for you';
    } else if (m.includes('added tokens') || m.includes('tokens added') || m.includes('token')) {
      return 'Tokens Added Successfully';
    } else {
      return 'New Notification';
    }
  }

  getTitleClass(message: string): string {
    const title = this.getNotificationTitle(message);
    switch (title) {
      case 'There is a new offer for you':
        return 'title-new-offer';
      case 'There is an updated offer for you':
        return 'title-updated-offer';
      case 'Your Bid Was Accepted':
        return 'title-bid-accepted';
      case 'Your Bid Was Rejected':
        return 'title-bid-rejected';
      case 'There is a reminder for you':
        return 'title-bid-reminder';
      case 'Tokens Added Successfully':
        return 'title-bid-tokens';
      default:
        return '';
    }
  }

  extractDetails(message: string): any {
    const units = this.extractValue(message, /(\d+\.?\d*)\s*kWh/i);
    let bidRateRaw = this.extractValue(message, /(\d+\.?\d*)\s*Rs/i);
    let bidRate = '-';
    if (bidRateRaw !== '-') {
      const parsed = parseFloat(bidRateRaw);
      bidRate = isNaN(parsed) ? '-' : parsed.toFixed(2);
    }
    const fromDate = this.extractDate(message, /from\s+([\w\s,:\-]+)\s+to/i);
    const toDate = this.extractDate(message, /to\s+([\w\s,:\-]+)/i);
    return { units, bidRate, fromDate, toDate };
  }

  private extractValue(message: string, regex: RegExp): string {
    if (!message) return '-';
    const match = message.match(regex);
    return match ? match[1] : '-';
  }

  private extractDate(message: string, regex: RegExp): string {
    if (!message) return '-';
    const match = message.match(regex);
    return match ? match[1] : '-';
  }
}